/**
 * 导演项目工作区（右侧主体）
 *
 * 接收 projectId，内部 hook 加载项目详情。
 * 包含：顶部 hero 状态条 + 资产抽屉触发 + 故事板 + 检查器 + 缩略图条 + 各种弹窗。
 *
 * 由 DirectorWorkbench 渲染（仅当用户选中了某个项目）。
 */

import { useEffect, useRef, useState } from 'react';
import {
  Plus,
  Loader2,
  Wand2,
  Film,
  Share2,
  FolderOpen,
  Sparkles,
  Menu,
  X as CloseIcon,
  ImageIcon,
  Users,
  MapPin,
  Package,
  Pencil,
  Trash2,
} from 'lucide-react';
import toast from '../../../components/Toast';
import { directorApi, DirectorApiError } from '../services/directorApi';
import { videoApi, type DurationSpec } from '../../../baselines/video/services/videoApi';
import { useDirectorProject } from '../hooks/useDirectorProject';
import { useCompositionPolling } from '../hooks/useCompositionPolling';
import { ShotCard } from './shots/ShotCard';
import { ShotInspector } from './shots/ShotInspector';
import { ShotThumbnailBar } from './shots/ShotThumbnailBar';
import { CharacterCreateDialog } from './characters/CharacterCreateDialog';
import { CharacterDetailDrawer } from './characters/CharacterDetailDrawer';
import { SceneCreateDialog } from './scenes/SceneCreateDialog';
import { SceneDetailDrawer } from './scenes/SceneDetailDrawer';
import { PropCreateDialog } from './props/PropCreateDialog';
import { PropDetailDrawer } from './props/PropDetailDrawer';
import { ActivateConfirmDialog } from './shots/ActivateConfirmDialog';
import { ActivateAllDialog } from './shots/ActivateAllDialog';
import { ShotSplitDialog } from './shots/ShotSplitDialog';
import { PreviewMode } from './composition/PreviewMode';
import { CompositionProgressBadge } from './composition/CompositionProgressBadge';
import { ShareDialog } from './share/ShareDialog';
import {
  ImportFromLibraryDialog,
  type LibraryImportTarget,
} from './library/ImportFromLibraryDialog';
import { AssetMetaEditDialog } from './shared/AssetMetaEditDialog';
import { withAuthToken } from '../../../utils/storageUrl';

interface Props {
  projectId: string;
  /** 当前用户余额 */
  credits: number;
  /** 项目状态变更后通知外层刷新侧栏 */
  onProjectChanged: () => void;
  /**
   * 外部触发的"重新加载本项目"信号。
   * 父级在 EditProjectDialog 保存（视频模型可能改变）时把 +1，
   * workspace 据此重新拉取项目，从而刷新分镜可选时长等派生 UI。
   */
  reloadSignal?: number;
}

const MAX_SHOTS_PER_PROJECT = 30;

export function DirectorProjectWorkspace({ projectId, credits, onProjectChanged, reloadSignal = 0 }: Props) {
  const [selectedShotId, setSelectedShotId] = useState<string | null>(null);

  // 角色 / 场景对话框
  const [showCreateCharacter, setShowCreateCharacter] = useState(false);
  const [openCharacterId, setOpenCharacterId] = useState<string | null>(null);
  const [showCreateScene, setShowCreateScene] = useState(false);
  const [openSceneId, setOpenSceneId] = useState<string | null>(null);

  // 物品对话框
  const [showCreateProp, setShowCreateProp] = useState(false);
  const [openPropId, setOpenPropId] = useState<string | null>(null);

  // 名称 / 描述独立编辑弹窗（角色 / 物品 / 场景 共用一个状态机）
  const [editAssetMeta, setEditAssetMeta] = useState<
    | { type: 'character'; id: string; name: string; description: string | null }
    | { type: 'prop'; id: string; name: string; description: string | null }
    | { type: 'scene'; id: string; name: string; description: string | null }
    | null
  >(null);

  // 激活
  const [activateConfirm, setActivateConfirm] = useState<{
    shotId: string;
    isReactivate: boolean;
    estimatedCredits: number;
  } | null>(null);
  const [activateSubmitting, setActivateSubmitting] = useState(false);
  const [showActivateAll, setShowActivateAll] = useState(false);

  // 智能拆分镜
  const [showShotSplit, setShowShotSplit] = useState(false);

  // 预览成片 / 分享
  const [showPreview, setShowPreview] = useState(false);
  const [showShare, setShowShare] = useState(false);

  // 作品库导入
  const [importTarget, setImportTarget] = useState<LibraryImportTarget | null>(null);

  // 窄屏左栏抽屉（资产）
  const [showAssetDrawer, setShowAssetDrawer] = useState(false);

  // 故事板横向滚动容器（点击缩略图条时自动居中到对应卡片）
  const storyboardScrollRef = useRef<HTMLDivElement | null>(null);

  // ===== 故事板拖拽重排状态 =====
  /** 正在被拖动的 shotId */
  const [draggingShotId, setDraggingShotId] = useState<string | null>(null);
  /** 当前拖拽悬停目标 shotId（显示插入指示） */
  const [dragOverShotId, setDragOverShotId] = useState<string | null>(null);
  /** 乐观重排覆盖：非 null 时优先用它渲染顺序（避免等后端 reload 的闪烁） */
  const [optimisticOrder, setOptimisticOrder] = useState<string[] | null>(null);

  /** 将故事板滚动到指定 shot 居中 */
  const scrollShotIntoCenter = (shotId: string) => {
    const container = storyboardScrollRef.current;
    if (!container) return;
    const card = container.querySelector<HTMLElement>(`[data-shot-id="${shotId}"]`);
    if (!card) return;
    const containerRect = container.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    // 计算卡片中心相对容器内容左侧的位置，再减去半个可视宽度即得居中 scrollLeft
    const offsetLeft = cardRect.left - containerRect.left + container.scrollLeft;
    const target = offsetLeft - container.clientWidth / 2 + card.clientWidth / 2;
    container.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  };

  /** 选中并自动居中（缩略图条点击使用） */
  const handleSelectAndCenter = (shotId: string) => {
    setSelectedShotId(shotId);
    // 等到下一帧再滚，避免命中尚未挂载的元素
    requestAnimationFrame(() => scrollShotIntoCenter(shotId));
  };

  const { project, loading, error, reload } = useDirectorProject(projectId);

  // 外部触发重新加载（例如 EditProjectDialog 改了视频模型，需要刷新分镜可选时长）
  // 跳过初次挂载（reloadSignal=0）
  const reloadSignalRef = useRef(reloadSignal);
  useEffect(() => {
    if (reloadSignal !== reloadSignalRef.current) {
      reloadSignalRef.current = reloadSignal;
      reload();
    }
  }, [reloadSignal, reload]);
  const compositionPolling = useCompositionPolling(projectId, { enabled: !!project });

  // 项目所选视频模型在 img2video 模式下的时长能力（按 adapter 声明）
  // null 表示尚未加载到，或加载失败；ShotVideoTab 会回退到通用 number 输入
  const [videoDurationSpec, setVideoDurationSpec] = useState<DurationSpec | null>(null);
  const [videoDurationDefault, setVideoDurationDefault] = useState<number | null>(null);

  useEffect(() => {
    if (!project?.videoModelId) {
      setVideoDurationSpec(null);
      setVideoDurationDefault(null);
      return;
    }
    let cancelled = false;
    videoApi
      .getPublicModels()
      .then((list) => {
        if (cancelled) return;
        const m = list.find((x) => x.modelId === project.videoModelId);
        const img2video = m?.modes.find((x) => x.mode === 'img2video');
        setVideoDurationSpec(img2video?.supportedDurations ?? null);
        setVideoDurationDefault(m?.defaults.duration ?? null);
      })
      .catch(() => {
        if (cancelled) return;
        setVideoDurationSpec(null);
        setVideoDurationDefault(null);
      });
    return () => {
      cancelled = true;
    };
  }, [project?.videoModelId]);

  useEffect(() => {
    setSelectedShotId(null);
  }, [projectId]);

  useEffect(() => {
    if (project && !selectedShotId && project.shots.length > 0) {
      setSelectedShotId(project.shots[0].id);
    }
  }, [project, selectedShotId]);

  // 自动轮询：有进行中视频任务时
  useEffect(() => {
    if (!project) return;
    const hasInFlight = project.shots.some(
      (s) =>
        s.activeVideoJob &&
        ['queued', 'pending', 'submitting', 'processing'].includes(s.activeVideoJob.status)
    );
    if (!hasInFlight) return;
    const t = setInterval(reload, 5000);
    return () => clearInterval(t);
  }, [project, reload]);

  const handleAddShot = async () => {
    if (!project) return;
    try {
      const shot = await directorApi.createShot(project.id);
      toast.success('分镜已添加');
      setSelectedShotId(shot.id);
      reload();
      onProjectChanged();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '添加失败');
    }
  };

  const handleMoveUp = async (shotId: string) => {
    try {
      await directorApi.moveShotUp(shotId);
      reload();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '操作失败');
    }
  };

  const handleMoveDown = async (shotId: string) => {
    try {
      await directorApi.moveShotDown(shotId);
      reload();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '操作失败');
    }
  };

  // ===== 拖拽重排 handlers =====
  const handleDragStart = (shotId: string) => {
    setDraggingShotId(shotId);
    setDragOverShotId(shotId);
  };

  const handleDragEnter = (overShotId: string) => {
    if (!draggingShotId || overShotId === draggingShotId) {
      setDragOverShotId(overShotId);
      return;
    }
    setDragOverShotId(overShotId);
  };

  const handleDragEnd = () => {
    setDraggingShotId(null);
    setDragOverShotId(null);
  };

  const handleDrop = async (targetShotId: string) => {
    const fromId = draggingShotId;
    setDraggingShotId(null);
    setDragOverShotId(null);
    if (!fromId || !project || fromId === targetShotId) return;

    const currentIds = project.shots.map((s) => s.id);
    const fromIdx = currentIds.indexOf(fromId);
    const toIdx = currentIds.indexOf(targetShotId);
    if (fromIdx < 0 || toIdx < 0) return;

    // 计算新顺序：把 fromId 移动到 targetShotId 的位置
    const next = [...currentIds];
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, fromId);

    // 顺序无变化则跳过
    if (next.every((id, i) => id === currentIds[i])) return;

    setOptimisticOrder(next);
    try {
      await directorApi.reorderShots(projectId, next);
      await reload();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '重排失败');
    } finally {
      setOptimisticOrder(null);
    }
  };

  const handleDuplicateShot = async (shotId: string) => {
    try {
      await directorApi.duplicateShot(shotId);
      toast.success('已复制');
      reload();
      onProjectChanged();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '复制失败');
    }
  };

  const handleReuseKeyframe = async (shotId: string) => {
    try {
      const newShot = await directorApi.reuseShotKeyframe(shotId);
      toast.success('已复用关键帧到新分镜，请填写 prompt');
      setSelectedShotId(newShot.id);
      reload();
      onProjectChanged();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '复用失败');
    }
  };

  const handleDeleteShot = async (shotId: string) => {
    // 先拿候选数 + active video 状态便于二次确认
    let confirmText = '确定要删除这个分镜吗？关联的 keyframe 候选和已生成的视频都会被隐藏。';
    try {
      const result = await directorApi.listKeyframes(shotId, { includeHidden: false });
      const candidateCount = result.jobs.length;
      const targetShot = project?.shots.find((s) => s.id === shotId);
      const videoStatus = targetShot?.activeVideoJob?.status;
      const items: string[] = [];
      if (candidateCount > 0) items.push(`${candidateCount} 张关键帧候选`);
      if (videoStatus === 'completed') items.push('1 条已激活视频');
      else if (
        videoStatus === 'queued' ||
        videoStatus === 'pending' ||
        videoStatus === 'submitting' ||
        videoStatus === 'processing'
      ) {
        items.push('1 条进行中的视频任务（未提交上游则退款）');
      }
      if (items.length > 0) {
        confirmText = `确定要删除这个分镜吗？\n将无法恢复：${items.join('、')}。`;
      }
    } catch {
      // 用通用文案
    }
    if (!window.confirm(confirmText)) return;
    try {
      await directorApi.deleteShot(shotId);
      toast.success('分镜已删除');
      if (selectedShotId === shotId) setSelectedShotId(null);
      reload();
      onProjectChanged();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '删除失败');
    }
  };

  // ---------------- 资产从项目移除（角色 / 场景 / 物品） ----------------
  // 统一行为：
  // 1) 先调 preview 拿到引用本资产的分镜数 + 列表
  // 2) 弹强 confirm，告知"会清理 N 个分镜的引用"
  // 3) 用户确认后调 remove API；后端会自动把 shot.injectedXxx 中本资产 ID 摘掉
  // 4) 资产本身保留在用户库里，不删

  const handleRemoveCharacterFromProject = async (characterId: string, name: string) => {
    let confirmText = `从本项目移除「${name}」？\n资产保留在你的角色库里，可重新加入。`;
    try {
      const preview = await directorApi.previewRemoveCharacterFromProject(projectId, characterId);
      if (preview.shotCount > 0) {
        const shotLabels = preview.shots
          .slice(0, 5)
          .map((s) => `#${s.shotOrderInProject + 1}${s.title ? ` · ${s.title}` : ''}`)
          .join('、');
        const more = preview.shots.length > 5 ? `等 ${preview.shotCount} 个分镜` : '';
        confirmText = `从本项目移除「${name}」？\n\n${preview.shotCount} 个分镜引用了它（${shotLabels}${more}），移除后这些引用会被自动清理。\n资产保留在你的角色库里，可重新加入。`;
      }
    } catch {
      // 预检失败不阻塞，走通用文案
    }
    if (!window.confirm(confirmText)) return;
    try {
      await directorApi.removeCharacterFromProject(projectId, characterId);
      toast.success('已从本项目移除');
      reload();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '移除失败');
    }
  };

  const handleRemoveSceneFromProject = async (sceneId: string, name: string) => {
    let confirmText = `从本项目移除「${name}」？\n资产保留在你的场景库里，可重新加入。`;
    try {
      const preview = await directorApi.previewRemoveSceneFromProject(projectId, sceneId);
      if (preview.shotCount > 0) {
        const shotLabels = preview.shots
          .slice(0, 5)
          .map((s) => `#${s.shotOrderInProject + 1}${s.title ? ` · ${s.title}` : ''}`)
          .join('、');
        const more = preview.shots.length > 5 ? `等 ${preview.shotCount} 个分镜` : '';
        confirmText = `从本项目移除「${name}」？\n\n${preview.shotCount} 个分镜引用了它（${shotLabels}${more}），移除后这些引用会被自动清理。\n资产保留在你的场景库里，可重新加入。`;
      }
    } catch {
      /* ignore */
    }
    if (!window.confirm(confirmText)) return;
    try {
      await directorApi.removeSceneFromProject(projectId, sceneId);
      toast.success('已从本项目移除');
      reload();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '移除失败');
    }
  };

  const handleRemovePropFromProject = async (propId: string, name: string) => {
    let confirmText = `从本项目移除「${name}」？\n资产保留在你的物品库里，可重新加入。`;
    try {
      const preview = await directorApi.previewRemovePropFromProject(projectId, propId);
      if (preview.shotCount > 0) {
        const shotLabels = preview.shots
          .slice(0, 5)
          .map((s) => `#${s.shotOrderInProject + 1}${s.title ? ` · ${s.title}` : ''}`)
          .join('、');
        const more = preview.shots.length > 5 ? `等 ${preview.shotCount} 个分镜` : '';
        confirmText = `从本项目移除「${name}」？\n\n${preview.shotCount} 个分镜引用了它（${shotLabels}${more}），移除后这些引用会被自动清理。\n资产保留在你的物品库里，可重新加入。`;
      }
    } catch {
      /* ignore */
    }
    if (!window.confirm(confirmText)) return;
    try {
      await directorApi.removePropFromProject(projectId, propId);
      toast.success('已从本项目移除');
      reload();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '移除失败');
    }
  };

  const handleActivate = async (
    shotId: string,
    options: { reactivate?: boolean } = {}
  ) => {
    try {
      const fn = options.reactivate ? directorApi.reactivateShot : directorApi.activateShot;
      const result = await fn(shotId);
      if (result.status === 'queued') {
        toast.success(`任务已排队（消耗 ${result.creditsUsed} 积分）`);
      } else {
        toast.success(`视频生成中（消耗 ${result.creditsUsed} 积分）`);
      }
      reload();
      onProjectChanged();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '激活失败');
    }
  };

  const openActivateConfirm = async (shotId: string, isReactivate: boolean) => {
    try {
      const preview = await directorApi.previewActivateShot(shotId);
      setActivateConfirm({
        shotId,
        isReactivate,
        estimatedCredits: preview.estimatedCredits,
      });
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '预估积分失败');
    }
  };

  const handleDeactivate = async (shotId: string) => {
    try {
      const result = await directorApi.deactivateShot(shotId);
      if (result.refunded) {
        toast.success('已取消任务，积分已退还');
      } else if (result.cancelledJobId) {
        toast.success('已取消（视频已生成成本不可逆）');
      } else {
        toast.success('无需取消');
      }
      reload();
      onProjectChanged();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '取消失败');
    }
  };

  const handleActivateAll = () => {
    if (!project || project.shots.length === 0) return;
    setShowActivateAll(true);
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="rounded-3xl bg-white/80 p-6 shadow-xl shadow-slate-200/80">
          <Loader2 className="h-8 w-8 animate-spin text-slate-900" />
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="flex h-full items-center justify-center px-4">
        <div className="rounded-[28px] border border-white/70 bg-white/85 p-8 text-center shadow-[0_24px_80px_rgba(15,23,42,0.08)] backdrop-blur-xl">
          <p className="mb-3 text-rose-500">{error || '项目加载失败'}</p>
          <p className="text-sm text-slate-500">请尝试刷新或选择其他项目</p>
        </div>
      </div>
    );
  }

  const selectedShot = project.shots.find((s) => s.id === selectedShotId) ?? null;
  const selectedActiveStatus = selectedShot?.activeVideoJob?.status ?? 'none';
  const selectedVideoActive =
    selectedActiveStatus === 'queued' ||
    selectedActiveStatus === 'pending' ||
    selectedActiveStatus === 'submitting' ||
    selectedActiveStatus === 'processing';

  // 故事板顺序：拖拽提交后、reload 完成前优先用 optimisticOrder（避免闪烁）
  const orderedShots = optimisticOrder
    ? optimisticOrder
        .map((id) => project.shots.find((s) => s.id === id))
        .filter((s): s is (typeof project.shots)[number] => !!s)
    : project.shots;
  // 项目合成中 / 归档时禁止拖拽重排
  const canDragReorder =
    project.status !== 'composing' && project.status !== 'archived';

  const completedCount = project.shots.filter(
    (s) => s.activeVideoJob?.status === 'completed'
  ).length;
  const totalShots = project.shots.length;
  const reachedShotLimit = project.shotCount >= MAX_SHOTS_PER_PROJECT;

  // 资产面板（左：角色 + 场景）
  const renderAssetPanel = (closeOnAction?: () => void) => (
    <div className="space-y-4">
      <div className="rounded-3xl border border-white/70 bg-white/85 p-4 shadow-[0_18px_50px_rgba(15,23,42,0.06)] backdrop-blur-xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
            <Users className="h-4 w-4 text-sky-500" /> 角色（{project.projectCharacters.length}）
          </h3>
          <button
            type="button"
            onClick={() => {
              setShowCreateCharacter(true);
              closeOnAction?.();
            }}
            className="rounded-xl p-1.5 text-slate-500 transition hover:bg-sky-50 hover:text-sky-600"
            title="新建/挑选角色"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
        {project.projectCharacters.length === 0 ? (
          <p className="text-xs text-slate-500">点上方 + 新建或从角色库挑选</p>
        ) : (
          <div className="space-y-1.5">
            {project.projectCharacters.map((pc) => (
              <div
                key={pc.characterId}
                className="group flex items-center gap-2 rounded-2xl bg-slate-50/80 transition hover:bg-sky-50"
              >
                <button
                  type="button"
                  onClick={() => {
                    setOpenCharacterId(pc.characterId);
                    closeOnAction?.();
                  }}
                  className="flex flex-1 items-center gap-2 px-2.5 py-2 text-left text-xs"
                >
                  {pc.character.portraitThumbnailUrl || pc.character.portraitUrl ? (
                    <img
                      src={withAuthToken(pc.character.portraitThumbnailUrl ?? pc.character.portraitUrl ?? '')}
                      alt=""
                      className="h-7 w-7 shrink-0 rounded-full object-cover ring-1 ring-sky-200"
                      loading="lazy"
                    />
                  ) : (
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sky-100 text-[11px] font-semibold text-sky-700">
                      {pc.character.name.slice(0, 1)}
                    </span>
                  )}
                  <span className="flex-1 truncate text-slate-800">{pc.character.name}</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditAssetMeta({
                      type: 'character',
                      id: pc.characterId,
                      name: pc.character.name,
                      description: pc.character.description,
                    });
                    closeOnAction?.();
                  }}
                  className="rounded-full p-1.5 text-slate-400 opacity-0 transition hover:bg-white hover:text-sky-600 group-hover:opacity-100"
                  title="编辑名称 / 描述"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setImportTarget({
                      type: 'character',
                      characterId: pc.characterId,
                      targetLabel: pc.character.name,
                    });
                    closeOnAction?.();
                  }}
                  className="rounded-full p-1.5 text-slate-400 opacity-0 transition hover:bg-white hover:text-sky-600 group-hover:opacity-100"
                  title="从作品库导入定妆照"
                >
                  <FolderOpen className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => handleRemoveCharacterFromProject(pc.characterId, pc.character.name)}
                  className="mr-1.5 rounded-full p-1.5 text-slate-400 opacity-0 transition hover:bg-rose-50 hover:text-rose-600 group-hover:opacity-100"
                  title="从本项目移除（资产保留）"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-3xl border border-white/70 bg-white/85 p-4 shadow-[0_18px_50px_rgba(15,23,42,0.06)] backdrop-blur-xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
            <MapPin className="h-4 w-4 text-cyan-500" /> 场景（{project.scenes.length}）
          </h3>
          <button
            type="button"
            onClick={() => {
              setShowCreateScene(true);
              closeOnAction?.();
            }}
            className="rounded-xl p-1.5 text-slate-500 transition hover:bg-sky-50 hover:text-sky-600"
            title="新建或从场景库挑选"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
        {project.scenes.length === 0 ? (
          <p className="text-xs text-slate-500">点上方 + 新建或从场景库挑选</p>
        ) : (
          <div className="space-y-1.5">
            {project.scenes.map((s) => (
              <div
                key={s.id}
                className="group flex items-center gap-2 rounded-2xl bg-slate-50/80 transition hover:bg-sky-50"
              >
                <button
                  type="button"
                  onClick={() => {
                    setOpenSceneId(s.id);
                    closeOnAction?.();
                  }}
                  className="flex flex-1 items-center gap-2 px-2.5 py-2 text-left text-xs"
                >
                  {s.settingThumbnailUrl || s.settingUrl ? (
                    <img
                      src={withAuthToken(s.settingThumbnailUrl ?? s.settingUrl ?? '')}
                      alt=""
                      className="h-6 w-8 shrink-0 rounded-md object-cover ring-1 ring-cyan-200"
                      loading="lazy"
                    />
                  ) : (
                    <span className="flex h-6 w-8 shrink-0 items-center justify-center rounded-md bg-cyan-100 text-cyan-700">
                      <MapPin className="h-3 w-3" />
                    </span>
                  )}
                  <span className="flex-1 truncate text-slate-800">{s.name}</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditAssetMeta({
                      type: 'scene',
                      id: s.id,
                      name: s.name,
                      description: s.description,
                    });
                    closeOnAction?.();
                  }}
                  className="rounded-full p-1.5 text-slate-400 opacity-0 transition hover:bg-white hover:text-sky-600 group-hover:opacity-100"
                  title="编辑名称 / 描述"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setImportTarget({
                      type: 'scene',
                      sceneId: s.id,
                      targetLabel: s.name,
                    });
                    closeOnAction?.();
                  }}
                  className="rounded-full p-1.5 text-slate-400 opacity-0 transition hover:bg-white hover:text-sky-600 group-hover:opacity-100"
                  title="从作品库导入设定图"
                >
                  <FolderOpen className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => handleRemoveSceneFromProject(s.id, s.name)}
                  className="mr-1.5 rounded-full p-1.5 text-slate-400 opacity-0 transition hover:bg-rose-50 hover:text-rose-600 group-hover:opacity-100"
                  title="从本项目移除（资产保留）"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 物品（道具）资产 */}
      <div className="rounded-3xl border border-white/70 bg-white/85 p-4 shadow-[0_18px_50px_rgba(15,23,42,0.06)] backdrop-blur-xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
            <Package className="h-4 w-4 text-amber-500" /> 物品（{project.projectProps.length}）
          </h3>
          <button
            type="button"
            onClick={() => {
              setShowCreateProp(true);
              closeOnAction?.();
            }}
            className="rounded-xl p-1.5 text-slate-500 transition hover:bg-amber-50 hover:text-amber-600"
            title="新建/挑选物品"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
        {project.projectProps.length === 0 ? (
          <p className="text-xs text-slate-500">点上方 + 新建或从物品库挑选</p>
        ) : (
          <div className="space-y-1.5">
            {project.projectProps.map((pp) => (
              <div
                key={pp.propId}
                className="group flex items-center gap-2 rounded-2xl bg-slate-50/80 transition hover:bg-amber-50"
              >
                <button
                  type="button"
                  onClick={() => {
                    setOpenPropId(pp.propId);
                    closeOnAction?.();
                  }}
                  className="flex flex-1 items-center gap-2 px-2.5 py-2 text-left text-xs"
                >
                  {pp.prop.portraitThumbnailUrl || pp.prop.portraitUrl ? (
                    <img
                      src={withAuthToken(pp.prop.portraitThumbnailUrl ?? pp.prop.portraitUrl ?? '')}
                      alt=""
                      className="h-7 w-7 shrink-0 rounded-full object-cover ring-1 ring-amber-200"
                    />
                  ) : (
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                      <Package className="h-3.5 w-3.5" />
                    </span>
                  )}
                  <span className="flex-1 truncate text-slate-800">{pp.prop.name}</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditAssetMeta({
                      type: 'prop',
                      id: pp.propId,
                      name: pp.prop.name,
                      description: pp.prop.description,
                    });
                    closeOnAction?.();
                  }}
                  className="rounded-full p-1.5 text-slate-400 opacity-0 transition hover:bg-white hover:text-amber-600 group-hover:opacity-100"
                  title="编辑名称 / 描述"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setImportTarget({
                      type: 'prop',
                      propId: pp.propId,
                      targetLabel: pp.prop.name,
                    });
                    closeOnAction?.();
                  }}
                  className="rounded-full p-1.5 text-slate-400 opacity-0 transition hover:bg-white hover:text-amber-600 group-hover:opacity-100"
                  title="从作品库导入主图"
                >
                  <FolderOpen className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => handleRemovePropFromProject(pp.propId, pp.prop.name)}
                  className="mr-1.5 rounded-full p-1.5 text-slate-400 opacity-0 transition hover:bg-rose-50 hover:text-rose-600 group-hover:opacity-100"
                  title="从本项目移除（资产保留）"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-[1380px] space-y-5 px-3 py-4 sm:px-6 sm:py-6">
      {/* 顶部 Hero 状态条 */}
      <section className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-5 text-white shadow-2xl shadow-slate-300/40 sm:rounded-[36px] sm:p-7">
        <div className="absolute right-[-100px] top-[-80px] h-72 w-72 rounded-full bg-sky-400/20 blur-3xl" />
        <div className="absolute bottom-[-80px] left-[-60px] h-64 w-64 rounded-full bg-cyan-400/15 blur-3xl" />
        <div className="relative grid gap-5 lg:grid-cols-[1fr_auto] lg:items-end">
          <div className="min-w-0">
            <div className="mb-3 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowAssetDrawer(true)}
                className="inline-flex items-center gap-1.5 rounded-2xl bg-white/10 px-2.5 py-1.5 text-xs font-medium text-white transition hover:bg-white/20 xl:hidden"
                aria-label="项目资产"
              >
                <Menu className="h-4 w-4" />
                <span>资产</span>
              </button>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-slate-200">
                <Sparkles className="h-3.5 w-3.5 text-sky-200" />
                导演项目
              </div>
            </div>
            <h1 className="break-words text-2xl font-semibold tracking-tight sm:text-3xl">
              {project.name}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-slate-300">
              <span>{totalShots} 个分镜</span>
              {totalShots > 0 && (
                <span className="inline-flex items-center gap-1.5">
                  <span>已激活</span>
                  <span className="font-semibold text-white">
                    {completedCount}/{totalShots}
                  </span>
                </span>
              )}
              {reachedShotLimit && (
                <span className="rounded-full bg-amber-300/15 px-2 py-0.5 text-amber-100">
                  已达 {MAX_SHOTS_PER_PROJECT} 上限
                </span>
              )}
              <CompositionProgressBadge
                composition={compositionPolling.composition}
                onClick={() => setShowPreview(true)}
              />
            </div>
            {project.description && (
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-300">
                {project.description}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleActivateAll}
              disabled={project.shots.length === 0}
              className="inline-flex items-center gap-1.5 rounded-2xl border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Wand2 className="h-3.5 w-3.5" /> 全部激活
            </button>
            <button
              type="button"
              onClick={() => setShowPreview(true)}
              disabled={project.shots.length === 0}
              className="inline-flex items-center gap-1.5 rounded-2xl bg-white px-4 py-2 text-sm font-semibold text-slate-950 transition hover:scale-[1.02] hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Film className="h-3.5 w-3.5" /> 预览成片
            </button>
            <button
              type="button"
              onClick={() => setShowShare(true)}
              disabled={!project.lastCompositionId}
              className="inline-flex items-center gap-1.5 rounded-2xl border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-40"
              title={!project.lastCompositionId ? '先生成成片才能分享' : ''}
            >
              <Share2 className="h-3.5 w-3.5" /> 分享
            </button>
          </div>
        </div>
      </section>

      {/* 三栏布局：资产 + 故事板 + 检查器 */}
      {/* 注意：中间列必须用 minmax(0,1fr) 而非 1fr，否则其 min-width=auto 会
          被内部 flex 卡片(每张 280px 且 shrink-0)的 min-content 撑开，
          导致故事板卡片越多页面越宽，overflow-x-auto 失效。 */}
      <div className="grid gap-5 xl:grid-cols-[260px_minmax(0,1fr)_360px]">
        {/* 左栏 */}
        <aside className="hidden xl:block">{renderAssetPanel()}</aside>

        {/* 中栏：故事板 */}
        <section className="min-w-0 space-y-4">
          <div className="relative overflow-hidden rounded-[26px] border border-white/70 bg-white/85 p-4 shadow-[0_18px_50px_rgba(15,23,42,0.06)] backdrop-blur-xl sm:p-5">
            {/* 装饰：左侧胶片暗条 */}
            <div className="pointer-events-none absolute inset-y-4 left-0 w-1 rounded-r-full bg-gradient-to-b from-sky-400/40 via-cyan-300/30 to-transparent" />

            <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-400 text-white shadow-md shadow-sky-200/60">
                  <Film className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <h3 className="flex items-center gap-2 text-[15px] font-semibold text-slate-900">
                    故事板
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                      {totalShots} / {MAX_SHOTS_PER_PROJECT}
                    </span>
                  </h3>
                  <p className="mt-0.5 text-[11px] text-slate-500">
                    {totalShots === 0
                      ? '点右侧"添加分镜"或"从剧本生成"开始；每个分镜可独立选择关键帧、帧序列或三合一模式'
                      : `已激活 ${completedCount} / ${totalShots}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowShotSplit(true)}
                  disabled={reachedShotLimit}
                  className="inline-flex items-center gap-1.5 rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-sky-200 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-40"
                  title={reachedShotLimit ? '已达分镜数上限' : '从剧本生成分镜'}
                >
                  <Wand2 className="h-3.5 w-3.5" /> 从剧本生成
                </button>
                <button
                  type="button"
                  onClick={handleAddShot}
                  disabled={reachedShotLimit}
                  className="inline-flex items-center gap-1.5 rounded-2xl bg-slate-950 px-3 py-1.5 text-xs font-semibold text-white shadow-md transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
                  title={reachedShotLimit ? `已达分镜数上限（${MAX_SHOTS_PER_PROJECT}）` : '添加新分镜'}
                >
                  <Plus className="h-3.5 w-3.5" /> 添加分镜
                </button>
              </div>
            </div>

            {/* 头部细进度条 */}
            {totalShots > 0 && (
              <div className="mb-4 h-1 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-sky-500 to-cyan-400 transition-all"
                  style={{
                    width: `${Math.round((completedCount / Math.max(totalShots, 1)) * 100)}%`,
                  }}
                />
              </div>
            )}

            {project.shots.length === 0 ? (
              <button
                type="button"
                onClick={handleAddShot}
                disabled={reachedShotLimit}
                className="flex w-full flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-white/40 py-14 text-sm text-slate-500 transition hover:border-sky-300 hover:bg-sky-50/40 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ImageIcon className="h-9 w-9 text-slate-300" />
                还没有分镜，点这里开始添加第一个镜头
              </button>
            ) : (
              <div
                ref={storyboardScrollRef}
                className="-mx-2 overflow-x-auto px-2 pb-2 [scrollbar-color:rgb(203_213_225)_transparent] [scrollbar-width:thin]"
              >
                <div className="flex items-stretch gap-3">
                  {(() => {
                    return orderedShots.map((shot, idx) => (
                      <ShotCard
                        key={shot.id}
                        shot={shot}
                        index={idx}
                        selected={shot.id === selectedShotId}
                        keyframeUrl={shot.lockedKeyframeUrl ?? null}
                        firstFrameUrl={shot.firstFrameUrl ?? null}
                        frameCount={shot.frameCount ?? 0}
                        trinityCoverUrl={shot.trinityCoverUrl ?? null}
                        trinityCardCount={shot.trinityCardCount ?? 0}
                        videoStatus={shot.activeVideoJob?.status ?? 'none'}
                        videoUrl={shot.activeVideoJob?.videoUrl ?? null}
                        videoModelName={shot.activeVideoJob?.modelName ?? null}
                        onSelect={() => setSelectedShotId(shot.id)}
                        onMoveUp={idx > 0 ? () => handleMoveUp(shot.id) : undefined}
                        onMoveDown={
                          idx < orderedShots.length - 1 ? () => handleMoveDown(shot.id) : undefined
                        }
                        onDuplicate={() => handleDuplicateShot(shot.id)}
                        onReuseKeyframe={() => handleReuseKeyframe(shot.id)}
                        onDelete={() => handleDeleteShot(shot.id)}
                        draggable={canDragReorder}
                        isDragging={draggingShotId === shot.id}
                        isDragOver={
                          dragOverShotId === shot.id && draggingShotId !== shot.id
                        }
                        onDragStart={() => handleDragStart(shot.id)}
                        onDragEnter={() => handleDragEnter(shot.id)}
                        onDragEnd={handleDragEnd}
                        onDrop={() => handleDrop(shot.id)}
                      />
                    ));
                  })()}

                  {/* 尾部：添加分镜占位卡 */}
                  <button
                    type="button"
                    onClick={handleAddShot}
                    disabled={reachedShotLimit}
                    className="group flex shrink-0 w-[260px] flex-col items-center justify-center gap-2 rounded-[22px] border border-dashed border-slate-300 bg-white/40 text-slate-500 transition hover:border-sky-400 hover:bg-sky-50/40 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-40"
                    title={reachedShotLimit ? `已达分镜数上限（${MAX_SHOTS_PER_PROJECT}）` : '添加新分镜'}
                  >
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-md ring-1 ring-slate-200 transition group-hover:scale-110 group-hover:ring-sky-200">
                      <Plus className="h-5 w-5" />
                    </span>
                    <span className="text-xs font-medium">添加分镜</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {project.shots.length > 0 && (
            <ShotThumbnailBar
              shots={orderedShots}
              selectedShotId={selectedShotId}
              onSelect={handleSelectAndCenter}
              draggable={canDragReorder}
              draggingShotId={draggingShotId}
              dragOverShotId={dragOverShotId}
              onDragStartShot={handleDragStart}
              onDragEnterShot={handleDragEnter}
              onDragEndShot={handleDragEnd}
              onDropShot={handleDrop}
            />
          )}
        </section>

        {/* 右栏：检查器（v2 自管理滚动 + sticky 状态条） */}
        <aside className="rounded-3xl border border-white/70 bg-white/85 p-4 shadow-[0_18px_50px_rgba(15,23,42,0.06)] backdrop-blur-xl xl:h-[calc(100vh-200px)]">
          {selectedShot ? (
            <ShotInspector
              shot={selectedShot}
              projectAspectRatio={project.aspectRatio}
              projectCharacters={project.projectCharacters.map((pc) => pc.character)}
              projectScenes={project.scenes}
              projectProps={project.projectProps.map((pp) => pp.prop)}
              videoActive={selectedVideoActive}
              activationStatus={selectedActiveStatus}
              videoUrl={selectedShot.activeVideoJob?.videoUrl ?? null}
              credits={credits}
              videoDurationSpec={videoDurationSpec}
              videoDurationDefault={videoDurationDefault}
              onShotUpdated={reload}
              onRequestActivate={() => openActivateConfirm(selectedShot.id, false)}
              onRequestReactivate={() => openActivateConfirm(selectedShot.id, true)}
              onRequestDeactivate={() => handleDeactivate(selectedShot.id)}
              onRequestImportKeyframe={() =>
                setImportTarget({
                  type: 'shot',
                  shotId: selectedShot.id,
                  targetLabel: `分镜 #${selectedShot.shotOrderInProject + 1}`,
                })
              }
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 py-12 text-sm text-slate-500">
              <Sparkles className="h-8 w-8 text-slate-300" />
              选择一个分镜查看详情
            </div>
          )}
        </aside>
      </div>

      {/* 窄屏资产抽屉 */}
      {showAssetDrawer && (
        <div className="fixed inset-0 z-[60] xl:hidden">
          <button
            type="button"
            aria-label="关闭"
            className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm"
            onClick={() => setShowAssetDrawer(false)}
          />
          <div className="absolute inset-y-0 right-0 w-[86vw] max-w-[340px] overflow-y-auto bg-[#eef3f8] p-4 shadow-2xl shadow-slate-950/25">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-900">项目资产</h2>
              <button
                type="button"
                onClick={() => setShowAssetDrawer(false)}
                className="rounded-full p-1.5 text-slate-500 transition hover:bg-white"
              >
                <CloseIcon className="h-4 w-4" />
              </button>
            </div>
            {renderAssetPanel(() => setShowAssetDrawer(false))}
          </div>
        </div>
      )}

      {/* 对话框 */}
      {showCreateCharacter && (
        <CharacterCreateDialog
          open={showCreateCharacter}
          projectId={projectId}
          existingCharacterIds={project.projectCharacters.map((pc) => pc.characterId)}
          onClose={() => setShowCreateCharacter(false)}
          onCreated={() => {
            setShowCreateCharacter(false);
            reload();
          }}
        />
      )}
      {openCharacterId && (
        <CharacterDetailDrawer
          open={!!openCharacterId}
          characterId={openCharacterId}
          projectAspectRatio={project.aspectRatio}
          onClose={() => setOpenCharacterId(null)}
          onCharacterUpdated={reload}
        />
      )}
      {showCreateScene && (
        <SceneCreateDialog
          open={showCreateScene}
          projectId={projectId}
          existingSceneIds={(project?.scenes ?? []).map((s) => s.id)}
          onClose={() => setShowCreateScene(false)}
          onCreated={() => {
            setShowCreateScene(false);
            reload();
          }}
        />
      )}
      {openSceneId && (
        <SceneDetailDrawer
          open={!!openSceneId}
          sceneId={openSceneId}
          projectAspectRatio={project.aspectRatio}
          onClose={() => setOpenSceneId(null)}
          onSceneUpdated={reload}
        />
      )}

      {showCreateProp && (
        <PropCreateDialog
          open={showCreateProp}
          projectId={projectId}
          existingPropIds={project.projectProps.map((pp) => pp.propId)}
          onClose={() => setShowCreateProp(false)}
          onCreated={() => {
            setShowCreateProp(false);
            reload();
          }}
        />
      )}
      {openPropId && (
        <PropDetailDrawer
          open={!!openPropId}
          propId={openPropId}
          projectAspectRatio={project.aspectRatio}
          onClose={() => setOpenPropId(null)}
          onPropUpdated={reload}
        />
      )}

      {editAssetMeta && (
        <AssetMetaEditDialog
          open
          title={
            editAssetMeta.type === 'character'
              ? '编辑角色'
              : editAssetMeta.type === 'prop'
                ? '编辑物品'
                : '编辑场景'
          }
          initialName={editAssetMeta.name}
          initialDescription={editAssetMeta.description}
          namePlaceholder={
            editAssetMeta.type === 'character'
              ? '角色名'
              : editAssetMeta.type === 'prop'
                ? '物品名'
                : '场景名'
          }
          descriptionPlaceholder={
            editAssetMeta.type === 'character'
              ? '例如：长发、白色长裙、温柔气质'
              : editAssetMeta.type === 'prop'
                ? '例如：维多利亚风格手柄，刃身有花纹'
                : '例如：维多利亚风客厅，烛光与雨夜'
          }
          accent={
            editAssetMeta.type === 'character'
              ? 'sky'
              : editAssetMeta.type === 'prop'
                ? 'amber'
                : 'cyan'
          }
          onClose={() => setEditAssetMeta(null)}
          onSave={async (next) => {
            try {
              if (editAssetMeta.type === 'character') {
                await directorApi.updateCharacter(editAssetMeta.id, next);
              } else if (editAssetMeta.type === 'prop') {
                await directorApi.updateProp(editAssetMeta.id, next);
              } else {
                await directorApi.updateScene(editAssetMeta.id, next);
              }
              toast.success('已保存');
              reload();
            } catch (err) {
              toast.error(err instanceof DirectorApiError ? err.message : '保存失败');
              throw err;
            }
          }}
        />
      )}

      {activateConfirm &&
        (() => {
          const targetShot = project.shots.find((s) => s.id === activateConfirm.shotId);
          if (!targetShot) return null;
          const activatePreview =
            targetShot.mode === 'frame_sequence'
              ? {
                  previewUrl: targetShot.firstFrameUrl ?? null,
                  sourceLabel: '帧序列',
                }
              : targetShot.mode === 'trinity_cards'
                ? {
                    previewUrl: targetShot.trinityCoverUrl ?? null,
                    sourceLabel: '三合一卡',
                  }
                : {
                    previewUrl: targetShot.lockedKeyframeUrl ?? null,
                    sourceLabel: '关键帧',
                  };
          return (
            <ActivateConfirmDialog
              open={!!activateConfirm}
              previewUrl={activatePreview.previewUrl}
              sourceLabel={activatePreview.sourceLabel}
              modelName={project.videoModelId}
              duration={targetShot.duration ?? 5}
              estimatedCredits={activateConfirm.estimatedCredits}
              currentCredits={credits}
              isReactivate={activateConfirm.isReactivate}
              submitting={activateSubmitting}
              onCancel={() => setActivateConfirm(null)}
              onConfirm={async () => {
                setActivateSubmitting(true);
                try {
                  await handleActivate(activateConfirm.shotId, {
                    reactivate: activateConfirm.isReactivate,
                  });
                  setActivateConfirm(null);
                } finally {
                  setActivateSubmitting(false);
                }
              }}
            />
          );
        })()}

      {showActivateAll && (
        <ActivateAllDialog
          open={showActivateAll}
          projectId={projectId}
          currentCredits={credits}
          onClose={() => setShowActivateAll(false)}
          onSubmitted={(result) => {
            toast.success(
              `已提交 ${result.videoJobIds.length} 个任务（其中 ${result.queuedCount} 个排队）`
            );
            reload();
            onProjectChanged();
          }}
        />
      )}

      {showShotSplit && (
        <ShotSplitDialog
          open={showShotSplit}
          projectId={projectId}
          onClose={() => setShowShotSplit(false)}
          onApplied={() => {
            setShowShotSplit(false);
            reload();
            onProjectChanged();
          }}
        />
      )}

      {showPreview && (
        <PreviewMode
          open={showPreview}
          projectId={projectId}
          onClose={() => {
            setShowPreview(false);
            reload();
            onProjectChanged();
          }}
        />
      )}

      {showShare && (
        <ShareDialog
          open={showShare}
          projectId={projectId}
          hasCompletedComposition={!!project.lastCompositionId}
          onClose={() => setShowShare(false)}
        />
      )}

      {importTarget && (
        <ImportFromLibraryDialog
          open={!!importTarget}
          target={importTarget}
          onClose={() => setImportTarget(null)}
          onImported={() => {
            reload();
          }}
        />
      )}
    </div>
  );
}
